import { useQuery } from '@apollo/client';
import { GetComplianceMonitoringAssessmentCountDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useIsModuleEnabled } from 'src/hooks/useIsModuleEnabled';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

/**
 * Gates the optional Oversight rating columns on the Risk Register.
 *
 * Returns true only when the oversight module is enabled for the org AND
 * either the user's role carries `read:compliance_monitoring_assessment` or
 * the user can see at least one oversight assessment via Hasura row-level
 * permissions (owner/contributor on ≥1 assessment). Module-off means no
 * columns for anyone.
 *
 * While the permission query is still loading, the aggregate count query is
 * deferred so role-permission users (who don't need the count) don't fire it
 * unnecessarily. Returns `false` during loading — column config will hydrate
 * once the decision is stable.
 */
export const useCanViewOversightColumns = (): boolean => {
  const oversightModuleEnabled = useIsModuleEnabled(
    'assessment.subModules.oversight_register_and_findings'
  );

  const { hasPermission: hasRolePermission, loading: permissionLoading } =
    useHasPermissionQuery('read:compliance_monitoring_assessment');

  const { data } = useQuery(GetComplianceMonitoringAssessmentCountDocument, {
    skip: !oversightModuleEnabled || permissionLoading || hasRolePermission,
    fetchPolicy: 'cache-first',
  });
  const hasContributions =
    (data?.compliance_monitoring_assessment_aggregate.aggregate?.count ?? 0) >
    0;

  if (permissionLoading) {
    return false;
  }

  return oversightModuleEnabled && (hasRolePermission || hasContributions);
};
